import React from 'react';
import styled from '@emotion/styled';
import { Link } from 'components/Link/Link';
import { Colors } from '@zapier/design-system';
import { mediaQueries } from 'components/mediaQueries';
import { useInView } from 'react-intersection-observer';
import { useTrackEvent } from 'tracking/useTrackEvent';
import paths from 'constants/paths';
import { UpNextArticle } from 'data/SingleArticle/types';
import { ArticleImage } from 'components/Images/ArticleImage/ArticleImage';

type Props = { article: UpNextArticle };

export const UpNext = ({ article }: Props) => {
  const { category, image, slug, shortDescription, title } = article;
  const trackEvent = useTrackEvent();

  const [ref, inView] = useInView();

  React.useEffect(() => {
    if (inView) {
      trackEvent('component_scrolled_into_view', {
        component: 'Up Next',
      });
    }
  }, [ref, inView]);

  return (
    <Link
      href={paths.blogArticle(slug)}
      onClick={(event) =>
        trackEvent('linkClicked', {
          linkText: title,
          href: paths.blogArticle(slug),
          path: window.location.pathname,
          posYFromTop: event.clientY,
        })
      }
    >
      <Outer ref={ref}>
        <Image image={image} alt={title} />
        <UpNextTag>Up Next</UpNextTag>
        <TextWrapper>
          <Category>{category[0]}</Category>
          <Title>{title}</Title>
          <ShortDescription>{shortDescription}</ShortDescription>
        </TextWrapper>
      </Outer>
    </Link>
  );
};

const Outer = styled.section`
  display: grid;
  position: relative;
  ${mediaQueries.tablet} {
    grid-template-columns: 300px 1fr;
    grid-gap: 30px;
  }
`;

const Image = styled(ArticleImage)`
  height: 200px;
  margin-bottom: 20px;
  width: 300px;
  ${mediaQueries.desktop} {
    margin-bottom: 0;
  }
`;

const UpNextTag = styled.div`
  align-items: center;
  background: linear-gradient(203.74deg, #ff4a00 42.15%, #ffc43e 101.17%);
  border-top-left-radius: 5px;
  color: ${Colors.white};
  display: flex;
  font-weight: 800;
  font-size: 12px;
  height: 30px;
  justify-content: center;
  line-height: 130%;
  position: absolute;
  text-transform: uppercase;
  width: 69px;
`;

const TextWrapper = styled.div`
  align-self: center;
`;

const Category = styled.p`
  color: ${Colors.blue500};
  font-weight: 600;
  font-size: 14px;
  line-height: 150%;
  margin-bottom: 10px;
`;

const Title = styled.strong`
  font-weight: 600;
  font-size: 20px;
  line-height: 140%;
  margin-bottom: 10px;
`;

const ShortDescription = styled.p`
  color: ${Colors.grey700};
  font-weight: normal;
  font-size: 16px;
  line-height: 150%;
`;
